
import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner, Tabs, Tab } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { FiCheckCircle, FiUserCheck, FiClock, FiMapPin, FiCamera } from 'react-icons/fi';
import api from '../services/api';
import FaceRecognition from '../components/FaceRecognition';

const Attendance = () => {
    const { token } = useParams();
    const [activeTab, setActiveTab] = useState('document');
    
    // Document Check-in States
    const [cpf, setCpf] = useState('');
    const [matricula, setMatricula] = useState('');
    
    // Face Check-in States
    const [isProcessingFace, setIsProcessingFace] = useState(false);
    
    // Common States
    const [loading, setLoading] = useState(false);
    const [trainingInfo, setTrainingInfo] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const requireCpf = trainingInfo?.double_check !== undefined ? trainingInfo.double_check : true;
    useEffect(() => {
        // Fetch Training Info
        const fetchInfo = async () => {
            try {
                const res = await api.get(`/public/training/${token}`);
                setTrainingInfo(res.data);
            } catch (err) {
                setError('Treinamento não encontrado ou link inválido.');
            }
        };
        fetchInfo();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setResult(null);
        const cpfDigits = (cpf || '').replace(/\D/g, '');
        const matriculaValue = (matricula || '').trim();

        if (requireCpf && !cpfDigits) {
            setError('CPF é obrigatório para este treinamento.');
            setLoading(false);
            return;
        }

        if (!requireCpf && !cpfDigits && !matriculaValue) {
            setError('Informe CPF ou Matrícula.');
            setLoading(false);
            return;
        }

        try {
            const res = await api.post('/public/attendance', {
                token,
                cpf: cpfDigits || undefined,
                matricula: matriculaValue || undefined
            });
            setResult(res.data);
            setCpf('');
            setMatricula('');
        } catch (err) {
            setError(err.response?.data?.msg || 'Erro ao registrar presença.');
        } finally {
            setLoading(false);
        }
    };
    
    const handleFaceCapture = async (imageSrc) => {
        setIsProcessingFace(true);
        setError('');
        setResult(null);
        
        // Convert base64 to blob
        const res = await fetch(imageSrc);
        const blob = await res.blob();
        const file = new File([blob], "face_capture.jpg", { type: "image/jpeg" });
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('token', token);
        
        try {
            const apiRes = await api.post('/public/checkin/face', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setResult(apiRes.data);
        } catch (err) {
             setError(err.response?.data?.msg || 'Rosto não reconhecido ou erro no servidor.');
        } finally {
            setIsProcessingFace(false);
        }
    };

    // CPF Mask
    const handleCpfChange = (e) => {
        let value = e.target.value.replace(/\D/g, '');
        if (value.length > 11) value = value.slice(0, 11);
        value = value.replace(/(\d{3})(\d)/, '$1.$2');
        value = value.replace(/(\d{3})(\d)/, '$1.$2');
        value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
        setCpf(value);
    };

    if (!trainingInfo && !error) return <div className="text-center mt-5"><Spinner animation="border" variant="primary" /></div>;

    return (
        <Container fluid className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', backgroundColor: '#eff2f6', fontFamily: "'Inter', sans-serif" }}>
            <Card className="shadow-lg border-0" style={{ width: '100%', maxWidth: '450px', borderRadius: '16px', overflow: 'hidden' }}>
                <div className="text-center pt-4 pb-2" style={{ backgroundColor: '#fff' }}>
                    <img src="/logo_texto_lateral.png" alt="Confirma AI" style={{ height: '50px', maxWidth: '80%' }} />
                </div>
                <Card.Header className="text-white text-center py-3 border-0" style={{ backgroundColor: '#00BFA5' }}>
                    <h4 className="mb-0 fw-bold" style={{ fontFamily: "'Nunito', sans-serif" }}>Registro de Presença</h4>
                    {trainingInfo && <p className="mb-0 mt-1" style={{ fontSize: '0.95rem', opacity: 0.9 }}>{trainingInfo.titulo}</p>}
                </Card.Header>
                <Card.Body className="p-4 bg-white">
                    {error && (
                        <Alert variant="danger" className="border-0 text-white" style={{ backgroundColor: '#FF8A65' }}>
                            {error}
                        </Alert>
                    )}
                    
                    {result ? (
                        <div className="text-center py-3">
                            <div className="mb-3">
                                <FiCheckCircle className="display-1" style={{ color: '#69F0AE' }} />
                            </div>
                            <h4 className="fw-bold mb-3" style={{ fontFamily: "'Nunito', sans-serif", color: '#00BFA5' }}>{result.msg}</h4>
                            <div className="p-3 rounded text-start mb-4" style={{ backgroundColor: '#F5F7FA', border: '1px solid #e0e0e0' }}>
                                <p className="mb-2"><strong>Participante:</strong> <span className="text-muted">{result.participant}</span></p>
                                <p className="mb-2"><strong>Horário:</strong> <span className="text-muted">{result.time}</span></p>
                                <p className="mb-0"><strong>Tipo:</strong> <span className="fw-bold" style={{ color: result.type === 'checkin' ? '#00BFA5' : '#FF8A65' }}>{result.type === 'checkin' ? 'Entrada' : 'Saída'}</span></p>
                            </div>
                            <Button 
                                variant="outline-primary" 
                                className="w-100 py-2 fw-bold" 
                                onClick={() => setResult(null)}
                                style={{ borderColor: '#00BFA5', color: '#00BFA5' }}
                            >
                                Novo Registro
                            </Button>
                        </div>
                    ) : (
                        <Tabs
                            activeKey={activeTab}
                            onSelect={(k) => { setActiveTab(k); setError(''); }}
                            className="mb-4 nav-justified"
                        >
                            <Tab eventKey="document" title={<><FiUserCheck className="me-2"/>Documento</>}>
                                <Form onSubmit={handleSubmit} className="mt-3">
                                    {trainingInfo && (
                                        <div className="mb-4 text-center text-muted small">
                                            <div className="d-inline-block me-3 mb-2">
                                                <FiClock className="me-1" style={{ color: '#FFD54F' }}/> 
                                                {new Date(trainingInfo.data).toLocaleString()}
                                            </div>
                                            <div className="d-inline-block mb-2">
                                                <FiMapPin className="me-1" style={{ color: '#FF8A65' }}/> 
                                                {trainingInfo.local}
                                            </div>
                                            <div className="mt-2 fst-italic" style={{ color: '#006064', fontSize: '0.8rem' }}>
                                                <small>
                                                    Tolerância: ±{trainingInfo.tolerance_checkin || 15}min (Check-in)
                                                    {trainingInfo.obriga_checkout && ` / ±${trainingInfo.tolerance_checkout || 15}min (Check-out)`}
                                                </small>
                                            </div>
                                            {!trainingInfo.obriga_checkout && (
                                                <div className="mt-1 text-muted" style={{ fontSize: '0.75rem' }}>
                                                    <small>* Este treinamento requer apenas registro de entrada.</small>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {requireCpf && (
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold text-muted small text-uppercase">CPF</Form.Label>
                                            <Form.Control 
                                                type="text" 
                                                placeholder="000.000.000-00" 
                                                value={cpf} 
                                                onChange={handleCpfChange}
                                                required
                                                className="p-3 text-center fs-5 border-0 bg-light"
                                                style={{ borderRadius: '10px', color: '#006064', fontWeight: 'bold' }}
                                            />
                                        </Form.Group>
                                    )}

                                    <Form.Group className="mb-4">
                                        <Form.Label className="fw-bold text-muted small text-uppercase">
                                            Matrícula (PIN){requireCpf ? '' : ' *'}
                                        </Form.Label>
                                        <Form.Control 
                                            type="text" 
                                            placeholder="Digite sua matrícula" 
                                            value={matricula} 
                                            onChange={(e) => setMatricula(e.target.value)}
                                            required={!requireCpf}
                                            className="p-3 text-center fs-5 border-0 bg-light"
                                            style={{ borderRadius: '10px', color: '#006064', fontWeight: 'bold' }}
                                        />
                                    </Form.Group>

                                    <Button 
                                        type="submit" 
                                        className="w-100 py-3 fw-bold border-0 shadow-sm" 
                                        disabled={loading}
                                        style={{ backgroundColor: '#69F0AE', color: '#004d40', fontSize: '1.1rem', borderRadius: '10px' }}
                                    >
                                        {loading ? <Spinner size="sm" animation="border" /> : <><FiUserCheck className="me-2"/> Confirmar Presença</>}
                                    </Button>
                                </Form>
                            </Tab>
                            <Tab eventKey="face" title={<><FiCamera className="me-2"/>Reconhecimento Facial</>}>
                                {activeTab === 'face' && (
                                    <div className="mt-3 text-center">
                                        <p className="text-muted small mb-3">
                                            Posicione seu rosto no centro da câmera. A captura será feita automaticamente.
                                        </p>
                                        <FaceRecognition 
                                            onCapture={handleFaceCapture} 
                                            isProcessing={isProcessingFace}
                                        />
                                    </div>
                                )}
                            </Tab>
                        </Tabs>
                    )}
                </Card.Body>
                <Card.Footer className="text-center text-muted small bg-white border-0 pb-4 pt-0">
                    <small>Confirma AI &copy; {new Date().getFullYear()}</small>
                </Card.Footer>
            </Card>
        </Container>
    );
};

export default Attendance;
